import { useEffect, useState } from 'react'

import { t } from 'commons/libs/i18n'
import { formatDate } from 'commons/utils/date'
import { Divider, Flex, Icon, Page, SlimButton, Text } from 'components'
import { type EdiRequest, EdiStatus, type EdiStatusOption } from 'constants/enums'
import { toast } from 'react-toastify'
import {
  fetchEdiRequestList,
  fetchEdiStatusOptions,
  postReprocessEdiOrdersBatch,
} from 'services/ediManagement'
import { theme } from 'styles/theme/hav-theme'

import { Filter } from './components/Filter'
import { ReprocessModal } from './components/ReprocessModal'
import { EdiTable } from './components/Table'
import { type EdiFilterValues, getDefaultEdiFilters, getEdiReprocessCode } from './helpers'
import * as S from './styles'

const DEFAULT_PAGE_SIZE = 20

export const EdiManagement = () => {
  const [ediRequests, setEdiRequests] = useState<EdiRequest[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const [statusOptions, setStatusOptions] = useState<EdiStatusOption[]>([])
  const [filters, setFilters] = useState<EdiFilterValues | null>(null)
  const [selectedEdiRequests, setSelectedEdiRequests] = useState<EdiRequest[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingStatusOptions, setIsLoadingStatusOptions] = useState<boolean>(true)
  const [isReprocessingBatch, setIsReprocessingBatch] = useState<boolean>(false)
  const [isReprocessModalOpen, setIsReprocessModalOpen] = useState<boolean>(false)

  const selectedFailedEdiRequests = selectedEdiRequests.filter(
    (item) => item.status === EdiStatus.FAIL,
  )

  const showBatchReprocessButton = selectedFailedEdiRequests.length > 0

  // TODO: Get total requests with errors from API
  const totalRequestsWithErrors = ediRequests.filter(
    (item) => item.status === EdiStatus.FAIL,
  ).length

  const getEdiRequestsData = async () => {
    if (!filters) return

    setIsLoading(true)

    try {
      const response = await fetchEdiRequestList({
        cnpj: filters.cnpj || undefined,
        customerOrders: filters.customerOrder
          ? filters.customerOrder
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : undefined,
        endDate: formatDate(filters.endDate, 'YYYY-MM-DD'),
        legalName: filters.legalName || undefined,
        page: currentPage,
        perPage: pageSize,
        sapCode: filters.sapCode || undefined,
        startDate: formatDate(filters.startDate, 'YYYY-MM-DD'),
        status: filters.status,
      })

      setEdiRequests(response.data)
      setTotalItems(response.pagination.totalItems)
      setSelectedEdiRequests([])
    } catch (_) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      setIsLoading(false)
    }
  }

  const getEdiStatusOptions = async () => {
    setIsLoadingStatusOptions(true)

    try {
      const options = await fetchEdiStatusOptions()

      setStatusOptions(options)
      setFilters(getDefaultEdiFilters(options))
    } catch (_) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      setIsLoadingStatusOptions(false)
    }
  }

  const handleBatchReprocess = () => {
    setIsReprocessModalOpen(true)
  }

  const handleConfirmBatchReprocess = async () => {
    try {
      setIsReprocessingBatch(true)

      await postReprocessEdiOrdersBatch({
        orderNumbers: selectedFailedEdiRequests.map(getEdiReprocessCode),
      })

      toast.success(t('screens.edi.messages.reprocessRequestSuccess'))
      setIsReprocessModalOpen(false)
      await getEdiRequestsData()
    } catch (_) {
      toast.error(t('screens.edi.messages.reprocessRequestError'))
    } finally {
      setIsReprocessingBatch(false)
    }
  }

  const handleSearch = (data: EdiFilterValues) => {
    setCurrentPage(1)
    setFilters(data)
  }

  const handleClearFilter = () => {
    setCurrentPage(1)
    setFilters(getDefaultEdiFilters(statusOptions))
  }

  const handleItemsPerPageChange = (items: number) => {
    setCurrentPage(1)
    setPageSize(items)
  }

  useEffect(() => {
    getEdiStatusOptions()
  }, [])

  useEffect(() => {
    getEdiRequestsData()
  }, [filters, currentPage, pageSize])

  return (
    <Page
      fullwidth
      loading={isLoading || isLoadingStatusOptions}
      renderHeaderComponent={
        <S.TitleBar>
          <S.Title>{t('screens.edi.title')}</S.Title>
        </S.TitleBar>
      }
    >
      <S.Container>
        {filters && (
          <Flex direction="column" gap="xm">
            <Flex alignItems="center" fullWidth justifyContent="space-between">
              <Flex fullWidth gap="sm">
                <Filter
                  onClear={handleClearFilter}
                  onSearch={handleSearch}
                  statusOptions={statusOptions}
                />
              </Flex>
            </Flex>

            <Divider />

            <Flex direction="column" fullWidth style={{ gap: theme.spacing.ion.xsmall }}>
              <Text size="xs" weight="md">
                {t('screens.edi.reprocessOrdersWithFailMessage')}
              </Text>
              <Flex direction="row" fullWidth style={{ gap: theme.spacing.ion.xsmall3 }}>
                <Text customColor={theme.colors.ion.primary.main} size="2xs" weight="sm">
                  {t('screens.edi.reprocessOrdersWithFailCount', {
                    amount: totalRequestsWithErrors,
                  })}
                </Text>
                <Text customColor={theme.colors.ion.neutral.strong} size="2xs" weight="sm">
                  {t('screens.edi.reprocessOrdersWithFailPeriod')}
                </Text>
              </Flex>
            </Flex>

            <S.ReprocessBatchContainer visible={showBatchReprocessButton}>
              <Text size="xs" weight="md">
                {t('screens.edi.whatDoYouWantToDoWithSelectedItems')}
              </Text>
              <SlimButton
                content={t('screens.edi.reprocessBatchButton')}
                disabled={isLoading || isReprocessingBatch || !showBatchReprocessButton}
                icon={<Icon icon="refresh-cw" size="16" />}
                loading={isReprocessingBatch}
                onClick={handleBatchReprocess}
                variant="primary"
              />
            </S.ReprocessBatchContainer>

            <EdiTable
              currentPage={currentPage}
              data={ediRequests}
              onClearFilter={handleClearFilter}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={setCurrentPage}
              onReprocessSuccess={getEdiRequestsData}
              onSelectionChange={setSelectedEdiRequests}
              pageSize={pageSize}
              totalItems={totalItems}
            />
          </Flex>
        )}
      </S.Container>
      <ReprocessModal
        loading={isReprocessingBatch}
        onConfirm={handleConfirmBatchReprocess}
        open={isReprocessModalOpen}
        setOpen={setIsReprocessModalOpen}
      />
    </Page>
  )
}
